/**
 * Utilitários para validação de reviews
 */

/**
 * Valida se todos os ratings obrigatórios foram preenchidos
 * @param {Object} review - O objeto review com os ratings
 * @param {Array} questions - Array de questões do questionário atual
 * @param {Function} isVisibleFn - Função para verificar se uma seção é visível para o perfil atual
 * @param {Function} toastShow - Função para exibir mensagens de erro
 * @returns {boolean} - true se todas as validações passaram, false caso contrário
 */
export function validateRequiredRatings(review, questions, isVisibleFn, toastShow) {
  // Função auxiliar para verificar se uma seção precisa de rating
  const isEngagementLeaderOrManagerSection = (section) => {
    return section === 'engagement_leader' ||
           section === 'manager' ||
           section === 'engagement_leader_for_quality' ||
           section === 'manager_for_quality' ||
           section === 'engagement_leader_for_inFlight' ||
           section === 'manager_for_inFlight';
  };

  const isReviewCommentsSection = (section) => {
    return section === 'review_comments';
  };

  let sectionsNeedingRating = [];
  let missingRatings = [];

  if (questions && questions.length > 0) {
    // Modo completo: usar questões disponíveis
    sectionsNeedingRating = questions.filter(questionSet => {
      const section = questionSet.questions[0].section;
      return !isEngagementLeaderOrManagerSection(section) &&
             !isReviewCommentsSection(section) &&
             isVisibleFn(section);
    });

    missingRatings = sectionsNeedingRating.filter(questionSet => {
      const section = questionSet.questions[0].section;
      const rating = review.ratings.find(r => r.section === section);
      return !rating || rating.rating === null || rating.rating === undefined;
    });

    // Verificar se o rating geral (final) é obrigatório e está faltando
    if (isVisibleFn('final')) {
      const finalRating = review.ratings.find(r => r.section === 'final');
      if (!finalRating || finalRating.rating === null || finalRating.rating === undefined) {
        missingRatings.push({
          section_text: 'Overall',
          questions: [{ section_text: 'Overall' }]
        });
      }
    }

    if (missingRatings.length > 0) {
      const missingSectionNames = missingRatings
        .map(qs => qs.section_text || qs.questions[0].section_text)
        .join(' and ');

      console.log(' ERRO: Ratings faltando para:', missingSectionNames);
      toastShow(
        `You must provide ratings for the following required sections: ${missingSectionNames}`
      );
      return false;
    }
  } else {
    // Modo simplificado: verificar apenas ratings existentes que são visíveis
    const existingRatings = review.ratings || [];
    missingRatings = existingRatings.filter(rating => {
      const section = rating.section;
      return !isEngagementLeaderOrManagerSection(section) &&
             !isReviewCommentsSection(section) &&
             isVisibleFn(section) &&
             (rating.rating === null || rating.rating === undefined);
    });

    // Verificar se o rating geral (final) é obrigatório e está faltando no modo simplificado
    if (isVisibleFn('final')) {
      const finalRating = existingRatings.find(r => r.section === 'final');
      if (!finalRating || finalRating.rating === null || finalRating.rating === undefined) {
        missingRatings.push({
          section: 'final'
        });
      }
    }

    if (missingRatings.length > 0) {
      const missingSectionNames = missingRatings
        .map(rating => {
          // Mapeamento básico de nomes de seções
          const sectionMap = {
            'documentation': 'Documentation',
            'performance_and_review': 'Performance & Review',
            'technical_documentation': 'Technical Documentation',
            'testing_and_quality_assurance': 'Testing and Quality Assurance',
            'final': 'Overall'
          };
          return sectionMap[rating.section] || rating.section;
        })
        .join(' and ');

      console.log(' ERRO (modo simplificado): Ratings faltando para:', missingSectionNames);
      toastShow(
        `You must provide ratings for the following required sections: ${missingSectionNames}`
      );
      return false;
    }
  }

  return true;
}

/**
 * Valida se um review pode ter seu status alterado para "Reviewer - Completed"
 * @param {Object} review - O objeto review
 * @param {number} newStatus - O novo status desejado
 * @param {Array} questions - Array de questões do questionário atual
 * @param {Function} isVisibleFn - Função para verificar se uma seção é visível para o perfil atual
 * @param {Function} toastShow - Função para exibir mensagens de erro
 * @returns {boolean} - true se pode alterar o status, false caso contrário
 */
export function validateStatusChange(review, newStatus, questions, isVisibleFn, toastShow) {
  // Se está tentando mudar para status 7 (Reviewer - Completed)
  if (newStatus === 7) {
    return validateRequiredRatings(review, questions, isVisibleFn, toastShow);
  }

  return true;
}
